import {SearchApiRequestTypes} from "../../config/constants"
import {FilterPriceApiResponse} from "../../models/Filter"
import {FacetsState, FacetState} from "../../models/FacetsState"
import {Filter} from "../getFiltersFromMerchandisedPath"

// URL PARTS
const SORT_URL_PART = "isort"
const FILTER_URL_PART = "af"
const PRICE_FILTER_URL_PART = "range"
const FILTER_URL_CATEGORY_PART = "f"
const MULTIPLE_FACET_SYMBOL = "%7E"

// URI components to passthrough to search banners
export const SEARCH_PARAMS_PASSTHROUGH = ["w", SORT_URL_PART, FILTER_URL_PART, PRICE_FILTER_URL_PART]

const SHOP_URL_PART = "shop"
let shopMerchandiseUrlPart: string
const REMOVABLE_CATEGORY_PART = "-0"

const chooseCategoryUrlBuilder = (
    uri: URL,
    sortValue: string,
    merchandisedUrl: boolean,
    previousUrl: string,
    facets?: FacetsState,
    priceFilter?: FilterPriceApiResponse,
    singleFacetFilters?: string[],
): URL => {
    if (merchandisedUrl) {
        return buildNewCategoryUrl(uri, sortValue, singleFacetFilters || [], facets, priceFilter, previousUrl)
    }
    return buildCategoryUrl(uri, sortValue, facets, priceFilter)
}

export const build = (
    previousUrl: string,
    sortValue: string,
    type: SearchApiRequestTypes,
    merchandisedUrl: boolean,
    facets?: FacetsState,
    priceFilter?: FilterPriceApiResponse,
    singleFacetFilters?: string[],
): string => {
    const uri = new URL(previousUrl)
    const formattedUri =
        type === SearchApiRequestTypes.Keyword
            ? buildKeywordUrl(uri, sortValue, facets, priceFilter)
            : chooseCategoryUrlBuilder(
                  uri,
                  sortValue,
                  merchandisedUrl,
                  previousUrl,
                  facets,
                  priceFilter,
                  singleFacetFilters,
              )

    return decodeQueryParam(formattedUri.toString())
}

function decodeQueryParam(p) {
    return decodeURIComponent(p.replace(/\+/g, " "))
}

const buildKeywordUrl = (
    uri: URL,
    sortValue: string,
    facets?: FacetsState,
    priceFilter?: FilterPriceApiResponse,
): URL => {
    const {searchParams} = uri
    searchParams.delete(FILTER_URL_PART)
    searchParams.delete(PRICE_FILTER_URL_PART)
    searchParams.delete(SORT_URL_PART)

    // Add URL filter part
    const queryParams = buildUrlFilterParams(formatKeywordFilterParam, "", facets)
    if (queryParams) {
        searchParams.append(FILTER_URL_PART, queryParams)
    }

    // Add URL sort part
    if (sortValue) {
        searchParams.append(SORT_URL_PART, sortValue)
    }

    // Add URL price filter part
    if (priceFilter && shouldDisplayPriceFilter(priceFilter)) {
        searchParams.append(
            PRICE_FILTER_URL_PART,
            `price%5b${convertPrice(priceFilter.selectedMin)},${convertPrice(priceFilter.selectedMax)}%5d`,
        )
    }
    // eslint-disable-next-line no-param-reassign
    uri.search = searchParams.toString()

    return uri
}

export const buildNewCategoryUrl = (
    uri: URL,
    sortValue: string,
    singleFacetFilters: string[],
    facets?: FacetsState,
    priceFilter?: FilterPriceApiResponse,
    previousUrl?: string,
): URL => {
    let parts = uri.pathname.toLowerCase().split("/")
    let completeUrl: string
    if (uri.pathname.includes("/shop")) shopMerchandiseUrlPart = "shop"
    if (uri.pathname.includes("/brands")) shopMerchandiseUrlPart = "brands"
    if (uri.pathname.includes("/promotions")) shopMerchandiseUrlPart = "promotions"

    const shopMerchandiseIndex = parts.indexOf(shopMerchandiseUrlPart)
    const categoryUrlSegment = parts[shopMerchandiseIndex + 1] // where cateogry starts.
    const updatedFacets = updateFacetsWithSingleFacetFilters(singleFacetFilters, facets || {}, previousUrl || "")
    const filterQueryParams = buildUrlFilterParams(formatNewCategoryFilterParam, categoryUrlSegment, updatedFacets) // active filters
    const sortSegmentIndex = parts.findIndex(part => part.includes(SORT_URL_PART))
    let _sortValue = sortValue

    /**
     * if the last segment starts with /isort then it is
     * /f/isort-xxx or /isort-xxx so we slice it off
     * then we can remove everything else after and the /f if it exists
     *
     * else if isort exists in a segment we split it out
     * then we can remove everything after and the /f if it exists
     */

    if (!_sortValue && parts[parts.length - 1].startsWith(SORT_URL_PART)) {
        // remove the sort segment
        ;[_sortValue] = parts.splice(parts.length - 1, 1)
        // eslint-disable-next-line prefer-destructuring
        _sortValue = _sortValue.split(`${SORT_URL_PART}-`)[1]
    } else if (!_sortValue && sortSegmentIndex > -1) {
        // eslint-disable-next-line prefer-destructuring
        _sortValue = parts[sortSegmentIndex].split(`${SORT_URL_PART}-`)[1]
    }

    // there are already filters, remove them
    const fIndex = parts.indexOf(FILTER_URL_CATEGORY_PART)
    if (fIndex > -1) {
        parts = parts.slice(0, fIndex)
    }

    let queryParams = filterQueryParams

    queryParams +=
        priceFilter && shouldDisplayPriceFilter(priceFilter)
            ? `${queryParams ? "-" : ""}minprice-${convertPrice(priceFilter.selectedMin)}-maxprice-${convertPrice(
                  priceFilter.selectedMax,
              )}`
            : ""

    if (shopMerchandiseIndex >= 0) {
        if (categoryUrlSegment) {
            if (categoryUrlSegment !== FILTER_URL_CATEGORY_PART) {
                parts[shopMerchandiseIndex + 1] = categoryUrlSegment.replace(REMOVABLE_CATEGORY_PART, "")
            }
        }
        parts.push(queryParams || _sortValue ? FILTER_URL_CATEGORY_PART : "") // add the f segment if there are filters to apply
        parts.push(queryParams) // add the filters
        parts = parts.filter((x, idx) => (idx === 0 ? true : x !== "")) // remove any empty segments except the first
    }

    completeUrl = parts.join("/")
    if (_sortValue) {
        const departmentUrlPart = completeUrl.split(`/${SORT_URL_PART}`)[0] // get everything before the sort
        completeUrl = departmentUrlPart
        completeUrl += `${queryParams ? "-" : "/"}${SORT_URL_PART}-${_sortValue.replace(/%20+/g, "")}`
    }

    // eslint-disable-next-line no-param-reassign
    uri.pathname = completeUrl
    // eslint-disable-next-line no-param-reassign
    uri.search = "" // Resetting the search parameter empty to display the first page of the next filtered results.
    // eslint-disable-next-line no-param-reassign
    uri.hash = "" // Resetting uri.hash to avoid page scroll
    return uri
}

const buildCategoryUrl = (
    uri: URL,
    sortValue: string,
    facets?: FacetsState,
    priceFilter?: FilterPriceApiResponse,
): URL => {
    const parts = uri.pathname.split("/")
    const pathIndex = parts.findIndex(part => part.toLowerCase() === SHOP_URL_PART)
    const categoryUrlSegment = parts[pathIndex + 1]

    let queryParams = buildUrlFilterParams(formatOldCategoryFilterParam, categoryUrlSegment, facets)
    queryParams +=
        priceFilter && shouldDisplayPriceFilter(priceFilter)
            ? `${queryParams ? "-" : ""}minprice-${convertPrice(priceFilter.selectedMin)}-maxprice-${convertPrice(
                  priceFilter.selectedMax,
              )}`
            : ""
    if (sortValue) {
        queryParams += `${queryParams ? "-" : ""}${SORT_URL_PART}-${sortValue}`
    }

    if (pathIndex >= 0) {
        parts[pathIndex + 1] = categoryUrlSegment.replace(REMOVABLE_CATEGORY_PART, "")
        parts[pathIndex + 2] = queryParams
    }
    // eslint-disable-next-line no-param-reassign
    uri.pathname = parts.join("/")
    // eslint-disable-next-line no-param-reassign
    uri.search = "" // Resetting the search parameter empty to display the first page of the next filtered results.
    // eslint-disable-next-line no-param-reassign
    uri.hash = "" // Resetting uri.hash to avoid page scroll
    return uri
}

const buildUrlFilterParams = (
    formatFilterParam: (q: string, f: [string, FacetState]) => string,
    categoryUrlSegment?: string,
    facets?: FacetsState,
): string => {
    let queryString = ""
    if (facets) {
        queryString = Object.entries(facets)
            .filter(f => f[1].s === true && !categoryUrlSegment?.includes(f[1].v.replace(":", "-")))
            .sort((a, b) => {
                if (a < b) {
                    return -1
                }
                if (a > b) {
                    return 1
                }
                return 0
            })
            .reduce(formatFilterParam, "")
    }
    return queryString
}

const formatNewCategoryFilterParam = (queryString: string, filter: [string, FacetState]) => {
    const splitFilter = filter[1].v.split(":")
    const filterName = splitFilter[0]
    const facetName = splitFilter[1].replace(/\s|-/g, "")
    const filterParamString = `${queryString}${
        queryString.includes(`${filterName}-`) ? MULTIPLE_FACET_SYMBOL : `-${filterName}-`
    }${facetName}`

    if (filterParamString.startsWith("-")) {
        return filterParamString.slice(1)
    }
    return filterParamString
}

const formatOldCategoryFilterParam = (queryString: string, filter: [string, FacetState]) =>
    `${queryString}${queryString ? "-" : ""}${filter[1].v.replace(":", "-")}`
const formatKeywordFilterParam = (queryString: string, filter: [string, FacetState]) =>
    `${queryString}${queryString ? " " : ""}${filter[1].v}`

const convertPrice = (price: number) => {
    return price * 100
}

export const shouldDisplayPriceFilter = (price?: FilterPriceApiResponse) => {
    if (!price) return false
    return price.selectedMin !== price.min || price.max !== price.selectedMax
}

export default build

export const getPageOneUri = (historyUrl: string) => {
    const uri = new URL(historyUrl)
    const {searchParams} = uri
    searchParams.set("p", "1")
    uri.search = searchParams.toString()
    uri.hash = ""

    return decodeQueryParam(uri.toString())
}

export const getScrollPosition = (historyUrl: string): number => {
    const fragment = new URL(historyUrl).hash
    const scrollPosition = parseInt(fragment.replace(/[^0-9]/g, ""), 10)
    return Number.isNaN(scrollPosition) ? 0 : scrollPosition
}

export const updateFacetsWithSingleFacetFilters = (
    singleFacetFilterList: string[],
    facets: FacetsState,
    url: string,
): FacetsState => {
    const urlFilters = url.split("/f/")[1]
    if (!urlFilters) return facets

    const updatedFacets = {...facets}
    singleFacetFilterList.forEach(singleFacetFilter => {
        const filter = extractSingleFacetFilterFromUrl(singleFacetFilter, url)
        if (!filter) return
        const filterFacetName = `${filter.filterName}:${filter.filterValue}`
        if (!(filterFacetName in facets)) {
            updatedFacets[filterFacetName] = {s: true, v: filterFacetName} as any as FacetState
        }
    })
    return updatedFacets
}

export const extractSingleFacetFilterFromUrl = (singleFacetFilterName: string, url: string): Filter | null => {
    const regex = `${singleFacetFilterName}-\\w*`
    if (!url.match(regex)) return null
    const [filterName, filterValue] = (url.match(regex) || [""])[0].split("-")
    return {filterName, filterValue}
}
